/* eslint-disable class-methods-use-this */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module bulb-audio/bulbaudio
 */

import { Plugin, ButtonView } from 'ckeditor5';
import type { Locale, ModelWriter } from 'ckeditor5';

// Import any custom assets that we need. This is going to be the plugin button image:
import bulbDeleteBlockIcon from '../../assets/toolbar_icons/delete_block_icon.svg';
import { windowsEventListenerChecklist } from '../utils';

export class BulbDeleteBlockUI extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbDeleteBlockUI' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { model } = editor;
    const { t } = editor;

    // The "simpleBox" button must be registered among the UI components of the editor
    // to be displayed in the toolbar.
    editor.ui.componentFactory.add('bulbDeleteBlock', (locale: Locale): ButtonView => {
      // The button will be an instance of ButtonView.
      const buttonView = new ButtonView(locale);

      buttonView.set({
        label: t('editor_button_delete'),
        icon: bulbDeleteBlockIcon,
        tooltip: true,
      });

      buttonView.on('execute', () => {
        model.change(() => {
          this.deleteDialog(editor);
        });
      });

      return buttonView;
    });

    // Listener for showing confirmation modal when pressing delete key.
    // Refer editor.component.ts 'configureKeydownEvent' method

    if (!windowsEventListenerChecklist.deleteBlockAdded) {
      windowsEventListenerChecklist.deleteBlockAdded = true;
      window.addEventListener('bulbShowBlockDeleteConfirmation', this.deleteDialog);
      window.addEventListener('deleteSelectedBlock', this.deleteBlock, false);
      // Listener for showing delete confirmation modal when No button is pressed
      // on upload failed.
      window.addEventListener('deleteFailedUploadBlock', () => {
        this.deleteDialog(editor);
      });
    }
  }

  /**
   * Delete block on delete key.
   * @param event
   */
  private deleteBlockOnKey(event: CustomEvent) {
    this.deleteDialog(event.detail.editor);
  }

  /**
   * Delete block on confirmation.
   * @param event
   */
  private deleteBlock(event: CustomEvent) {
    const { model } = event.detail;
    const { editor } = event.detail;
    model.change((writer: ModelWriter) => {
      const block = model.document.selection.getSelectedElement();
      if (event.detail.text) {
        editor.execute('insertParagraph', {
          position: editor.model.createPositionAt(block, 'before'),
        });
        const editingView = editor.editing.view;
        editingView.focus();
        const insertPosition = editor.model.document.selection.getFirstPosition();
        writer.insertText(event.detail.text, insertPosition);
      }
      writer.remove(block);
      const id = block.getAttribute('bulb-block-id');
      const deletedBlockEvent = new CustomEvent('blockElementUnload', {
        detail: {
          blockId: id,
          blockName: block.name,
        },
      });
      window.dispatchEvent(deletedBlockEvent);
    });
  }

  /**
   * Open delete block dialog.
   * @param editorOrEvent Event which will contains details of block.
   */
  private deleteDialog(editorOrEvent) {
    let editor;
    let imageCarouselSlide;
    if (editorOrEvent && editorOrEvent.detail) {
      editor = editorOrEvent.detail.editor;
      if (editorOrEvent.detail.imageCarouselSlide) {
        imageCarouselSlide = editorOrEvent.detail.imageCarouselSlide;
      }
    } else {
      editor = editorOrEvent;
    }
    const block = editor.model.document.selection.getSelectedElement();
    const name = block?.name;
    let type: string;
    switch (name) {
      case 'table':
        type = 'TABLE';
        break;
      case 'horizontalLine':
        type = 'DIVIDER';
        break;
      case 'bulbUploadPlaceholder':
        type = block.getAttribute('asset-type');
        break;
      default:
        {
          const id = block.getAttribute('bulb-block-id');
          const contentBlock = CKEBulbData.page.unit.contentBlocks[id];
          type = contentBlock.blockType;
        }
        break;
    }

    // Blur the focused editor to disable typing in editor when dialog is open.
    (document.activeElement as HTMLElement).blur();

    // Open the currently selected block that we are going to delete:
    CKEBulbData.service.dialog.showDeleteBlock(type, editor.model, imageCarouselSlide);
  }

  /**
   * Remove event on plugin destroy.
   */
  public override destroy(): void {
    window.removeEventListener('deleteSelectedBlock', this.deleteBlock);
    windowsEventListenerChecklist.deleteBlockAdded = false;
    window.removeEventListener('bulbShowBlockDeleteConfirmation', this.deleteDialog);
  }
}
